import {
  IconNameEnum,
  useActivitiesStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreActivitiesComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  ActivityLocationAutocomplete,
  DateRangePicker,
  useActivityAvailabilitySearch,
} from "@tenant/activities";
import {
  Button,
  ButtonTypeEnum,
  Dialog,
  DialogTrigger,
  DialogContent,
  Icon,
  DialogClose,
} from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./ActivityAvailabilitySearch.module.scss";

const COMPONENT_KEY = CoreActivitiesComponent.ActivityAvailabilitySearch;

export type ActivityAvailabilitySearchProps = PlatformComponentProps;

export const ActivityAvailabilitySearch = ({
  children,
  className,
}: ActivityAvailabilitySearchProps) => {
  const styles = useActivitiesStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { context, handlers, text } = useActivityAvailabilitySearch();
  const { t } = useI18nContext();

  const isFormValid = context.place != null && context.dateRange?.from != null;

  const search = (activeSearch: boolean) => (
    <div className={cn("search")}>
      <ActivityLocationAutocomplete
        label={text.locationLabel}
        value={context.place}
        onChange={(val) => handlers.handlePlaceChange(val, activeSearch)}
        id={"activity-location-autocomplete"}
      />
      <DateRangePicker
        startDate={context.dateRange?.from}
        endDate={context.dateRange?.until}
        onStartDateChangeHandler={(date) => {
          handlers.handleDateRangeFromChange(date);
        }}
        onEndDateChangeHandler={(date) => {
          handlers.handleDateRangeUntilChange(date);
        }}
        onClose={() => handlers.handleDateRangeClose(activeSearch)}
      />
    </div>
  );

  return (
    <div className={clsx(block, className)}>
      <div className={cn("desktop-search")}>
        {search(false)}
        <Button
          className={cn("search-again-button")}
          buttonType={ButtonTypeEnum.Primary}
          onClick={handlers.handleSearchClick}
          disabled={!isFormValid}
        >
          {t("searchControl.searchAgain")}
        </Button>
      </div>
      <Dialog modal={false}>
        <DialogTrigger className={cn("search-button")}>
          <div className={cn("icon")}>
            <Icon iconName={IconNameEnum.search} />
          </div>
          <div className={cn("content")}>
            <div className={cn("search-button-label")}>{text.locationData}</div>
            <div className={cn("search-button-value")}>
              {text.searchSummary}
            </div>
          </div>
        </DialogTrigger>
        <DialogContent
          fullScreen
          bottomFixedContent={
            <DialogClose asChild>
              <button
                className={cn("submit-search-button")}
                onClick={handlers.handleSearchClick}
              >
                {text.search}
              </button>
            </DialogClose>
          }
        >
          {search(false)}
        </DialogContent>
      </Dialog>
      {children}
    </div>
  );
};
